import React, { useEffect, useRef, useCallback } from 'react';
import { useThemeStore, type Theme } from '@/stores/theme-store';

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    opacity: number;
    color: string;
    life: number;
    maxLife: number;
    angle?: number;
    speed?: number;
    trail?: Array<{ x: number; y: number; createdAt: number }>;
}

const TRAIL_LIFETIME_MS = 1100;
const TRAIL_SAMPLE_INTERVAL_MS = 42;
const MAX_TRAIL_POINTS = 28;
const FRAME_DURATION_MS = 1000 / 60;

// Theme-specific configurations
const themeConfigs: Record<Theme, {
    particleCount: number;
    colors: string[];
    speed: number;
    sizeRange: [number, number];
    style: 'stars' | 'fireflies' | 'bubbles' | 'grid' | 'ribbons' | 'ember' | 'none';
    cursorEffect: 'attract' | 'repel' | 'none';
    glowIntensity: number;
    trail: boolean;
}> = {
    default: {
        particleCount: 0,
        colors: [],
        speed: 0,
        sizeRange: [0, 0],
        style: 'none',
        cursorEffect: 'none',
        glowIntensity: 0,
        trail: false,
    },
    midnight: {
        particleCount: 80,
        colors: ['#ffffff', '#a78bfa', '#c4b5fd', '#818cf8'],
        speed: 0.15,
        sizeRange: [1, 3],
        style: 'stars',
        cursorEffect: 'attract',
        glowIntensity: 0.6,
        trail: true,
    },
    aurora: {
        particleCount: 150,
        colors: ['#22d3ee', '#34d399', '#a78bfa', '#f472b6', '#818cf8'],
        speed: 0.5,
        sizeRange: [2, 4],
        style: 'ribbons',
        cursorEffect: 'attract',
        glowIntensity: 0.7,
        trail: false,
    },
    ocean: {
        particleCount: 200,
        colors: ['#0ea5e9', '#06b6d4', '#22d3ee', '#67e8f9', '#38bdf8'],
        speed: 0.8,
        sizeRange: [2, 5],
        style: 'bubbles',
        cursorEffect: 'repel',
        glowIntensity: 0.5,
        trail: false,
    },
    forest: {
        particleCount: 60,
        colors: ['#4ade80', '#a3e635', '#fbbf24', '#84cc16'],
        speed: 0.1,
        sizeRange: [2, 5],
        style: 'fireflies',
        cursorEffect: 'attract',
        glowIntensity: 0.9,
        trail: true,
    },
    sunset: {
        particleCount: 120,
        colors: ['#f472b6', '#fb923c', '#fbbf24', '#f97316', '#ef4444'],
        speed: 0.3,
        sizeRange: [2, 5],
        style: 'ember',
        cursorEffect: 'attract',
        glowIntensity: 0.8,
        trail: true,
    },
    cyber: {
        particleCount: 100,
        colors: ['#22d3ee', '#f472b6', '#a78bfa', '#4ade80'],
        speed: 0.4,
        sizeRange: [1, 2],
        style: 'grid',
        cursorEffect: 'attract',
        glowIntensity: 1,
        trail: true,
    },
    minimal: {
        particleCount: 0,
        colors: [],
        speed: 0,
        sizeRange: [0, 0],
        style: 'none',
        cursorEffect: 'none',
        glowIntensity: 0,
        trail: false,
    },
};

export function InteractiveBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const mouseRef = useRef({ x: 0, y: 0, active: false });
    const animationRef = useRef<number>();
    const lastFrameTimeRef = useRef<number>();
    const { theme, interactiveEnabled } = useThemeStore();

    const config = themeConfigs[theme];

    const createParticle = useCallback((x?: number, y?: number): Particle => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return {
                x: 0, y: 0, vx: 0, vy: 0, size: 1,
                opacity: 1, color: '#fff', life: 0, maxLife: 1000
            };
        }

        const [minSize, maxSize] = config.sizeRange;
        const size = minSize + Math.random() * (maxSize - minSize);
        const colorIndex = Math.floor(Math.random() * config.colors.length);
        const color = config.colors[colorIndex] ?? '#ffffff';

        const particle: Particle = {
            x: x ?? Math.random() * canvas.width,
            y: y ?? Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * config.speed,
            vy: (Math.random() - 0.5) * config.speed,
            size,
            opacity: 0.5 + Math.random() * 0.3, // Constant higher opacity
            color,
            life: 0,
            maxLife: Infinity, // Particles live forever
            trail: config.trail ? [] : undefined,
        };

        // Style-specific initialization
        switch (config.style) {
            case 'stars':
                particle.vy = Math.random() * 0.2;
                particle.opacity = 0.6 + Math.random() * 0.4;
                break;
            case 'fireflies':
                particle.opacity = 0.7 + Math.random() * 0.3;
                break;
            case 'bubbles':
                // Distribute across entire screen, floating randomly
                particle.x = Math.random() * canvas.width;
                particle.y = Math.random() * canvas.height;
                // Random direction movement
                particle.vx = (Math.random() - 0.5) * config.speed;
                particle.vy = (Math.random() - 0.5) * config.speed;
                particle.opacity = 0.5 + Math.random() * 0.4;
                break;
            case 'ribbons':
                // Aurora-like flowing ribbons across entire screen
                particle.x = Math.random() * canvas.width;
                particle.y = Math.random() * canvas.height;
                particle.angle = Math.random() * Math.PI * 2;
                particle.speed = config.speed * (0.5 + Math.random() * 0.5);
                particle.opacity = 0.5 + Math.random() * 0.4;
                break;
            case 'ember':
                // Floating sparks across entire screen
                particle.x = Math.random() * canvas.width;
                particle.y = Math.random() * canvas.height;
                particle.vx = (Math.random() - 0.5) * config.speed;
                particle.vy = (Math.random() - 0.5) * config.speed;
                particle.opacity = 0.6 + Math.random() * 0.4;
                break;
            case 'grid':
                particle.vx = (Math.random() > 0.5 ? 1 : -1) * config.speed;
                particle.vy = (Math.random() > 0.5 ? 1 : -1) * config.speed;
                break;
        }

        return particle;
    }, [config]);

    const updateParticle = useCallback((
        particle: Particle,
        canvas: HTMLCanvasElement,
        now: number,
        frameScale: number
    ) => {
        particle.life += frameScale;

        if (config.trail) {
            const trail = particle.trail ?? (particle.trail = []);
            const lastPoint = trail[trail.length - 1];
            if (!lastPoint || now - lastPoint.createdAt >= TRAIL_SAMPLE_INTERVAL_MS) {
                trail.push({ x: particle.x, y: particle.y, createdAt: now });
            }
            while (trail.length > 0 && now - trail[0]!.createdAt >= TRAIL_LIFETIME_MS) {
                trail.shift();
            }
            if (trail.length > MAX_TRAIL_POINTS) {
                trail.splice(0, trail.length - MAX_TRAIL_POINTS);
            }
        }

        // Mouse interaction
        if (mouseRef.current.active && config.cursorEffect !== 'none') {
            const dx = mouseRef.current.x - particle.x;
            const dy = mouseRef.current.y - particle.y;
            const distanceSquared = dx * dx + dy * dy;

            if (distanceSquared > 1 && distanceSquared < 150 * 150) {
                const dist = Math.sqrt(distanceSquared);
                const force = (150 - dist) / 150;
                const direction = config.cursorEffect === 'attract' ? 1 : -1;
                particle.vx += (dx / dist) * force * 0.045 * direction * frameScale;
                particle.vy += (dy / dist) * force * 0.045 * direction * frameScale;
            }
        }

        // Style-specific updates
        switch (config.style) {
            case 'stars':
                // Keep constant opacity, no pulsing
                break;
            case 'fireflies':
                // Gentle random movement
                particle.vx += (Math.random() - 0.5) * 0.02 * frameScale;
                particle.vy += (Math.random() - 0.5) * 0.02 * frameScale;
                break;
            case 'bubbles':
                // Gentle random movement, wrap around screen
                particle.vx += (Math.random() - 0.5) * 0.02 * frameScale;
                particle.vy += (Math.random() - 0.5) * 0.02 * frameScale;
                // Clamp velocity
                particle.vx = Math.max(-config.speed, Math.min(config.speed, particle.vx));
                particle.vy = Math.max(-config.speed, Math.min(config.speed, particle.vy));
                break;
            case 'ribbons': {
                // Flowing motion with curves
                const angle = particle.angle || 0;
                const speed = particle.speed || config.speed;
                particle.vx = Math.cos(angle + Math.sin(particle.life * 0.01) * 0.5) * speed;
                particle.vy = Math.sin(angle + Math.cos(particle.life * 0.01) * 0.5) * speed * 0.5;
                particle.angle = angle + (Math.random() - 0.5) * 0.02 * frameScale;
                break;
            }
            case 'ember':
                // Gentle floating movement
                particle.vx += (Math.random() - 0.5) * 0.02 * frameScale;
                particle.vy += (Math.random() - 0.5) * 0.02 * frameScale;
                break;
            case 'grid':
                // Bounce off edges
                if (particle.x <= 0 || particle.x >= canvas.width) particle.vx *= -1;
                if (particle.y <= 0 || particle.y >= canvas.height) particle.vy *= -1;
                break;
        }

        // Apply velocity with damping
        particle.x += particle.vx * frameScale;
        particle.y += particle.vy * frameScale;
        const damping = Math.pow(0.99, frameScale);
        particle.vx *= damping;
        particle.vy *= damping;

        // Wrap around screen (except for grid which bounces)
        if (config.style !== 'grid') {
            let wrapped = false;
            if (particle.x < -50) { particle.x = canvas.width + 50; wrapped = true; }
            if (particle.x > canvas.width + 50) { particle.x = -50; wrapped = true; }
            if (particle.y < -50) { particle.y = canvas.height + 50; wrapped = true; }
            if (particle.y > canvas.height + 50) { particle.y = -50; wrapped = true; }
            // Never draw a trail across the whole viewport after wrapping.
            if (wrapped && particle.trail) particle.trail.length = 0;
        }

        return particle.life < particle.maxLife;
    }, [config]);

    const drawParticle = useCallback((ctx: CanvasRenderingContext2D, particle: Particle) => {
        if (config.trail && particle.trail && particle.trail.length > 1) {
            const trail = particle.trail;
            const firstPoint = trail[0]!;
            const lastPoint = trail[trail.length - 1]!;
            ctx.save();
            ctx.globalAlpha = particle.opacity * 0.34;
            ctx.lineWidth = Math.max(0.75, particle.size * 0.7);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
                const gradient = ctx.createLinearGradient(
                    firstPoint.x,
                    firstPoint.y,
                    lastPoint.x,
                    lastPoint.y
                );
                gradient.addColorStop(0, `${particle.color}00`);
                gradient.addColorStop(0.45, `${particle.color}35`);
                gradient.addColorStop(1, particle.color);
                ctx.strokeStyle = gradient;
            } else {
                ctx.strokeStyle = particle.color;
            }

            ctx.beginPath();
            ctx.moveTo(firstPoint.x, firstPoint.y);
            for (let index = 1; index < trail.length; index++) {
                const point = trail[index]!;
                ctx.lineTo(point.x, point.y);
            }
            ctx.lineTo(particle.x, particle.y);
            ctx.stroke();
            ctx.restore();
        }

        ctx.save();

        switch (config.style) {
            case 'stars':
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = particle.color;
                ctx.globalAlpha = particle.opacity;
                ctx.shadowBlur = particle.size * 3 * config.glowIntensity;
                ctx.shadowColor = particle.color;
                ctx.fill();
                break;

            case 'fireflies': {
                const fireflyGradient = ctx.createRadialGradient(
                    particle.x, particle.y, 0,
                    particle.x, particle.y, particle.size * 3
                );
                fireflyGradient.addColorStop(0, particle.color);
                fireflyGradient.addColorStop(1, 'transparent');
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
                ctx.fillStyle = fireflyGradient;
                ctx.globalAlpha = particle.opacity;
                ctx.fill();
                break;
            }

            case 'bubbles': {
                // Draw bubble with highlight
                const bubbleGradient = ctx.createRadialGradient(
                    particle.x - particle.size * 0.3, particle.y - particle.size * 0.3, 0,
                    particle.x, particle.y, particle.size
                );
                bubbleGradient.addColorStop(0, particle.color + '80');
                bubbleGradient.addColorStop(0.7, particle.color + '30');
                bubbleGradient.addColorStop(1, particle.color + '10');
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = bubbleGradient;
                ctx.globalAlpha = particle.opacity;
                ctx.shadowBlur = particle.size * 2 * config.glowIntensity;
                ctx.shadowColor = particle.color;
                ctx.fill();
                // Bubble ring
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.strokeStyle = particle.color;
                ctx.globalAlpha = particle.opacity * 0.5;
                ctx.lineWidth = 0.5;
                ctx.stroke();
                break;
            }

            case 'ribbons': {
                // Draw flowing ribbon trail
                const ribbonGradient = ctx.createRadialGradient(
                    particle.x, particle.y, 0,
                    particle.x, particle.y, particle.size * 4
                );
                ribbonGradient.addColorStop(0, particle.color);
                ribbonGradient.addColorStop(0.5, particle.color + '40');
                ribbonGradient.addColorStop(1, 'transparent');
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
                ctx.fillStyle = ribbonGradient;
                ctx.globalAlpha = particle.opacity;
                ctx.shadowBlur = particle.size * 4 * config.glowIntensity;
                ctx.shadowColor = particle.color;
                ctx.fill();
                break;
            }

            case 'ember': {
                // Draw glowing ember/spark
                const emberGradient = ctx.createRadialGradient(
                    particle.x, particle.y, 0,
                    particle.x, particle.y, particle.size * 2
                );
                emberGradient.addColorStop(0, '#ffffff');
                emberGradient.addColorStop(0.2, particle.color);
                emberGradient.addColorStop(0.5, particle.color + '80');
                emberGradient.addColorStop(1, 'transparent');
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
                ctx.fillStyle = emberGradient;
                ctx.globalAlpha = particle.opacity;
                ctx.shadowBlur = particle.size * 6 * config.glowIntensity;
                ctx.shadowColor = particle.color;
                ctx.fill();
                break;
            }

            case 'grid':
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = particle.color;
                ctx.globalAlpha = particle.opacity;
                ctx.shadowBlur = particle.size * 5 * config.glowIntensity;
                ctx.shadowColor = particle.color;
                ctx.fill();
                break;
        }

        ctx.restore();
    }, [config]);

    const drawGridConnections = useCallback((ctx: CanvasRenderingContext2D) => {
        if (config.style !== 'grid') return;

        const particles = particlesRef.current;
        let connectionCount = 0;
        ctx.save();
        ctx.lineWidth = 0.5;

        // Each pair is checked once. Limiting the number of visible links keeps
        // the cost predictable on large/high-resolution screens.
        for (let index = 0; index < particles.length && connectionCount < 240; index++) {
            const particle = particles[index]!;
            for (let otherIndex = index + 1; otherIndex < particles.length && connectionCount < 240; otherIndex++) {
                const other = particles[otherIndex]!;
                const dx = other.x - particle.x;
                const dy = other.y - particle.y;
                const distanceSquared = dx * dx + dy * dy;
                if (distanceSquared >= 100 * 100) continue;

                const distance = Math.sqrt(distanceSquared);
                ctx.beginPath();
                ctx.moveTo(particle.x, particle.y);
                ctx.lineTo(other.x, other.y);
                ctx.strokeStyle = particle.color;
                ctx.globalAlpha = (1 - distance / 100) * 0.2;
                ctx.stroke();
                connectionCount++;
            }
        }

        ctx.restore();
    }, [config.style]);

    const animate = useCallback((timestamp: number) => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const previousTimestamp = lastFrameTimeRef.current ?? timestamp;
        // Movement remains consistent even if a frame is delayed, without
        // allowing a background tab to create a giant jump on resume.
        const frameScale = Math.min(2.5, Math.max(0.25, (timestamp - previousTimestamp) / FRAME_DURATION_MS));
        lastFrameTimeRef.current = timestamp;

        if (document.hidden) {
            animationRef.current = requestAnimationFrame(animate);
            return;
        }

        // Every frame starts clean. Trails are finite, timestamped samples
        // redrawn above, so they fade smoothly and can never leave stale pixels.
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Update once, draw connections behind particles, then draw each
        // particle. Particles currently live forever, so no per-frame arrays
        // need to be allocated here.
        for (const particle of particlesRef.current) {
            updateParticle(particle, canvas, timestamp, frameScale);
        }
        drawGridConnections(ctx);
        for (const particle of particlesRef.current) {
            drawParticle(ctx, particle);
        }

        // Maintain particle count
        while (particlesRef.current.length < config.particleCount) {
            particlesRef.current.push(createParticle());
        }

        animationRef.current = requestAnimationFrame(animate);
    }, [config.particleCount, createParticle, updateParticle, drawParticle, drawGridConnections]);

    useEffect(() => {
        if (!interactiveEnabled || config.style === 'none') {
            // Clear canvas when disabled
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (ctx && canvas) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Initialize particles
        particlesRef.current = [];
        for (let i = 0; i < config.particleCount; i++) {
            particlesRef.current.push(createParticle());
        }

        // Mouse tracking
        const handleMouseMove = (e: MouseEvent) => {
            mouseRef.current.x = e.clientX;
            mouseRef.current.y = e.clientY;
            mouseRef.current.active = true;
        };

        const handleMouseLeave = () => {
            mouseRef.current.active = false;
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseleave', handleMouseLeave);

        // Start animation
        lastFrameTimeRef.current = undefined;
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseleave', handleMouseLeave);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            lastFrameTimeRef.current = undefined;
        };
    }, [theme, interactiveEnabled, config, createParticle, animate]);

    if (!interactiveEnabled || config.style === 'none') {
        return null;
    }

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none z-0"
            style={{ opacity: 0.4 }}
        />
    );
}
